import { Controller } from "@hotwired/stimulus"
import { useTransition, useClickOutside } from "stimulus-use"
import Rails from "@rails/ujs"
import { template, debounce } from "lodash"
import { forceSetValueOnSelect, trySetValue, setValueAndDispatch } from "./helpers"

export default class extends Controller {
  static targets = ["menu", "input", "list", "resourceTemplate", "blank", "searchQuerySlot", "addWrapper", "gusWrapper",
    "outsideClose", "item"]

  static values = {
    company: Boolean,
    kind: String,
    type: String,
    useRecipient: Boolean,
    income: Boolean,
    translations: Object,
    identityCardFields: Array,
    index: { type: Number, default: 0 },
    companyChoosable: Boolean,
    prefix: String,
    mode: { type: String, default: "modal" },
    url: String,
  }

  static outlets = ["invoice", "delivery-address", "dropdown-company-search"]

  eventCreated = false
  clientData = []
  inputValue = ""
  prefix

  connect() {
    if (this.hasMenuTarget) {
      this.menuTarget.classList.add("hidden")
      this.resourceTemplate = template(this.resourceTemplateTarget.innerHTML)

      useTransition(this, { element: this.menuTarget })
      this.triggerSearch = debounce(this.triggerSearch, 500).bind(this)
    }

    if (this.hasOutsideCloseTarget) {
      useClickOutside(this, {
        element: this.outsideCloseTarget
      })
    }

    this.setPrefix()
  }

  setPrefix() {
    switch (this.typeValue) {
      case "buyer":
        this.prefix = "invoice_buyer"
        break
      case "recipient":
        this.prefix = `invoice_recipients_attributes_${this.indexValue}`
        break
      case "issuer":
        this.prefix = `invoice_issuers_attributes_${this.indexValue}`
        break
      case "client":
        this.prefix = `${this.prefixValue}_client`
        break
      default:
        throw new Error(`unknown type: ${this.typeValue}`)
    }
  }

  open() {
    this.toggle()
    this.search()
  }

  triggerSearch(event) {
    this.listTarget.innerHTML = ""
    if (this.modeValue === "inline" && event.target.value.length < 2) {
      this.hide()
      return
    }

    this.inputValue = event.target.value
    this.search()
  }

  search() {
    Rails.ajax({
      url: this.urlValue,
      type: "post",
      data: this.prepareParams(),
      success: (data) => this.showResults(data),
      error: (err) => console.error("Błąd w autocomplete", err)
    })
  }

  prepareParams() {
    const params = {
      rows: 10,
      name: this.inputValue
    }

    if (this.typeValue !== "issuer") {
      params.kind = this.kindValue
    }

    if (this.companyChoosableValue) {
      params.person = !this.companyValue
    }

    return new URLSearchParams(params)
  }

  getFromGUS() {
    if (this.hasDropdownCompanySearchOutlet) {
      this.hide()
      this.dropdownCompanySearchOutlet.inputTarget.value = this.inputValue
      this.dropdownCompanySearchOutlet.submit()
    }
  }

  showResults(data) {
    const hasResults = data.length > 0

    if (this.modeValue === "inline") {
      this.renderItems(data)
      this.searchQuerySlotTargets.forEach(el => { el.textContent = this.inputValue })
      this.menuTarget.classList.toggle("hidden", false)

      const isNip = !hasResults && this.hasGusWrapperTarget ? this.isValidNip() : false

      this.addWrapperTarget.classList.toggle("hidden", isNip)
      this.gusWrapperTarget.classList.toggle("hidden", !isNip)
    } else {
      this.renderItems(data)
      this.listTarget.classList.toggle("hidden", !hasResults)

      if (this.hasBlankTarget) {
        this.blankTarget.classList.toggle("hidden", hasResults)
      }
    }
  }

  renderItems(data) {
    this.listTarget.innerHTML = ""
    this.clientData = data
    data.forEach((client, index) => {
      this.itemData = {
        key: index,
        client: client.label,
        name: client.value
      }
      this.listTarget.innerHTML += this.resourceTemplate(this.itemData)
    })
  }

  select(event) {
    event.preventDefault()
    const clientIndex = event.params.value
    if (!this.clientData[clientIndex]) return
    if (this.modeValue === "inline") {
      this.menuTarget.classList.toggle("hidden", true)
    }

    switch (this.typeValue) {
      case "recipient":
        if (this.clientData[clientIndex].buyer !== undefined) {
          if (confirm(this.translationsValue["fetch_buyer_confirm"] + this.clientData[clientIndex].buyer.label + "?")) {
            document.querySelector("#invoice_buyer_name").value = this.clientData[clientIndex].buyer.value
            this.selectType(this.clientData[clientIndex].buyer, "buyer")
            this.fillBuyerFields(this.clientData[clientIndex].buyer)
          }
        }
        trySetValue(`#${this.prefix}_client_id`, this.clientData[clientIndex].id)
        this.fillContractorFields("recipients", this.clientData[clientIndex])
        break
      case "issuer":
        trySetValue(`#${this.prefix}_contractor_id`, this.clientData[clientIndex].id)
        this.fillContractorFields("issuers", this.clientData[clientIndex])
        break
      case "buyer":
        if ((this.incomeValue && this.useRecipientValue)) {
          if (this.clientData[clientIndex].buyer !== undefined && confirm(this.translationsValue["fetch_buyer_confirm"] + this.clientData[clientIndex].buyer.label + "?")) {
            this.indexValue = parseInt(document.querySelectorAll("[data-invoice--contractors-target='list'] > [data-invoice--contractors-index-value]").length)
            document.querySelector("[data-target='buttonRecipient'] > button").click()
            document.querySelector("#invoice_buyer_name").value = this.clientData[clientIndex].buyer.value
            this.fillBuyerFields(this.clientData[clientIndex].buyer)
            this.fillContractorFields("recipients", this.clientData[clientIndex])
          } else {
            this.fillBuyerFields(this.clientData[clientIndex])
          }
        } else {
          this.fillBuyerFields(this.clientData[clientIndex])
        }
        break
      case "client":
        this.fillClientFields(this.clientData[clientIndex])
        break
      default:
        throw new Error(`unknown type: ${this.typeValue}`)
    }

    this.hide()
    window.dispatchEvent(new CustomEvent("update-form"))
  }

  fillFields(prefix, data) {
    setValueAndDispatch(document.getElementById(`${prefix}_tax_no_kind`), data.tax_no_kind || "")
    setValueAndDispatch(document.getElementById(`${prefix}_tax_no`), data.tax_no)
    trySetValue(`#${prefix}_post_code`, data.post_code)
    trySetValue(`#${prefix}_city`, data.city)
    trySetValue(`#${prefix}_street`, data.street)
    const countryField = document.querySelector(`#${prefix}_country`)
    forceSetValueOnSelect(countryField, data.country)
    countryField?.dispatchEvent(new Event("change", { bubbles: true }))

    trySetValue(`#${prefix}_email`, data.email)
    trySetValue(`#${prefix}_phone`, data.phone)
    trySetValue(`#${prefix}_name`, data.value)
    trySetValue(`#${prefix}_first_name`, data.first_name)
    trySetValue(`#${prefix}_last_name`, data.last_name)

    if (data.note) {
      trySetValue(`#${prefix}_note`, data.note)
    }
  }

  fillBuyerFields(buyer) {
    this.fillFields("invoice_buyer", buyer)
    document.getElementById("invoice_client_id").value = buyer.id

    // powiadom listy powiązanych dokumentów (zaliczki/zamówienia) o zmianie nabywcy, żeby odświeżyły
    // preloadowanych kandydatów; dispatch tuż po ustawieniu client_id (dalsze linie mogą rzucić na mobile)
    document.dispatchEvent(new CustomEvent("invoice:client-changed", { detail: { clientId: buyer.id } }))

    if (this.hasInvoiceOutlet && this.invoiceOutlet.kindValue !== "kp") {
      trySetValue(`#invoice_buyer_person`, buyer.person)
    }

    trySetValue("#buyer_email_check", buyer.email)
    trySetValue(`#invoice_buyer_mobile_phone`, buyer.mobile_phone)

    if (document.getElementById("invoice_buyer_company")) {
      trySetValue(`#invoice_buyer_company`, buyer.company)
    } else {
      trySetValue(`#invoice_buyer_title`, buyer.title || "")
    }

    if (this.hasInvoiceOutlet && this.invoiceOutlet.accountUseMassPaymentValue) {
      document.getElementById("invoice_buyer_mass_payment_code").value = buyer.mass_payment_code
      document.getElementById("invoice_buyer_mass_payment_code").dispatchEvent(new Event("change"))
    }

    if (document.getElementById("invoice_buyer_bank")) {
      document.getElementById("invoice_buyer_bank").value = buyer.bank
      document.getElementById("invoice_buyer_bank_account").value = buyer.bank_account
    }

    if (this.hasDeliveryAddressOutlet && this.deliveryAddressOutlet.hasSelectTarget) {
      // multiple_delivery_addresses
      this.deliveryAddressOutlet.loadDeliveryAddresses(buyer.id)
    } else if (document.getElementById("invoice_use_delivery_address")) {
      document.getElementById("invoice_use_delivery_address").checked = buyer.use_delivery_address
      document.getElementById("invoice_use_delivery_address").dispatchEvent(new Event("change"))
      document.getElementById("invoice_delivery_address").value = buyer.delivery_address || ""
    }

    if (buyer.discount != null && this.invoiceOutlet.hasDiscountTarget) {
      if (this.invoiceOutlet.hasDiscountElemTarget && !this.invoiceOutlet.discountTarget.checked) {
        document.getElementById("show_discount_button").click()
      } else {
        this.invoiceOutlet.discountTarget.checked = true
      }

      document.querySelectorAll("[id^=invoice_positions_attributes_][id$='_discount_percent']").forEach((elem) => {
        elem.value = buyer.discount
      })
    }

    if (buyer.default_payment_type !== null && buyer.default_payment_type !== "") {
      forceSetValueOnSelect(document.getElementById("invoice_payment_type"), buyer.default_payment_type)
    }
    if (buyer.payment_to_kind !== null && buyer.payment_to_kind !== "") {
      forceSetValueOnSelect(document.getElementById("invoice_payment_to_kind"), buyer.payment_to_kind)
    }

    this.defaultTax = buyer.default_tax

    // vat_rr
    for (const field of this.identityCardFieldsValue) {
      if (!document.getElementById(`invoice_buyer_${field}`)) continue
      document.getElementById(`invoice_buyer_${field}`).value = buyer[field]
    }

    if (this.invoiceOutlet && this.invoiceOutlet.usePriceListValue) {
      this.invoiceOutlet.priceListTarget.value = buyer.price_list_id
    }

    if (this.hasInvoiceOutlet && this.invoiceOutlet.handleCondNoteValue) {
      this.invoiceOutlet.updateCondNote()
    }

    if (this.invoiceOutlet.hasBuyerPostalAddressWrapperTarget) {
      const postalAddress = buyer.postal_address
      const postalAddressWrapper = this.invoiceOutlet.buyerPostalAddressWrapperTarget
      const postalAddressElem = document.getElementById("buyer_postal_address_checkbox")
      if (postalAddress) {
        Object.entries(buyer.postal_address).forEach(([key, val]) => {
          document.querySelector(`#invoice_buyer_postal_address_attributes_${key}`).value = val
        })
      } else {
        postalAddressWrapper.querySelector("button").click()
      }

      postalAddressWrapper.querySelector(".delivery_address_old").value = buyer.delivery_address || ""
      postalAddressWrapper.querySelectorAll("[data-old-address]").forEach(elem => { elem.classList.toggle("hidden", !!postalAddress || !buyer.delivery_address) })

      postalAddressElem.checked = !!postalAddress
      postalAddressElem.dispatchEvent(new Event("change"))
    }

    //TODO if @invoice.jpk_pds_enabled? - start
    // if (client.tp_client_connection) {
    //   addInvoiceProcedureDesignation('TP');
    //   $.jGrowl("Klient powiązany z nabywcą - automatyczne dodanie kodu TP");
    // } else {
    //   handleRemovingTP();
    //   if(isPdCodePresent('TP')) {
    //     $.jGrowl("Zmiana klienta - automatyczne usunięcie kodu TP");
    //   }
    // }
    //TODO if @invoice.jpk_pds_enabled? - end

    this.invoiceOutlet?.fillWithDefaultTax()
  }

  fillContractorFields(prefix, contractor) {
    this.fillFields(`invoice_${prefix}_attributes_${this.indexValue}`, contractor)

    requestAnimationFrame(() => {
      this.selectType(contractor, `${prefix}_attributes_${this.indexValue}`)
    })

    if (this.invoiceOutlet.ksefEnabledValue) {
      // dla odbiorcy rola domyślna z karty klienta (Client#recipient_role)
      const role = prefix === "recipients" ? (contractor.role || contractor.recipient_role) : contractor.role
      trySetValue(`#invoice_${prefix}_attributes_${this.indexValue}_role`, role)
      trySetValue(`#invoice_${prefix}_attributes_${this.indexValue}_participation`, contractor.participation || "")
      document.getElementById(`invoice_${prefix}_attributes_${this.indexValue}_role`).dispatchEvent(new Event("change"))
    }
  }

  fillClientFields(client) {
    this.fillFields(this.prefix, client)
    document.getElementById(`${this.prefixValue}_client_id`).value = client.id

    if (document.getElementById(`${this.prefixValue}_client_title`)) {
      document.getElementById(`${this.prefixValue}_client_title`).value = client.title
    }

    if (document.getElementById(`${this.prefixValue}_client_title`)) {
      forceSetValueOnSelect(document.getElementById(`${this.prefixValue}_client_title`), client.title)
    }

    if (this.hasDeliveryAddressOutlet && this.deliveryAddressOutlet.hasSelectTarget) {
      this.deliveryAddressOutlet.loadDeliveryAddresses(client.id)
    } else if (client.use_delivery_address && document.getElementById(`${this.prefixValue}_client_use_delivery_address`)) {
      document.getElementById(`${this.prefixValue}_client_use_delivery_address`).checked = client.use_delivery_address
      document.getElementById(`${this.prefixValue}_client_use_delivery_address`).dispatchEvent(new Event("change"))
      document.getElementById(`${this.prefixValue}_client_delivery_address`).value = client.delivery_address
    }
  }

  selectType(obj, type) {
    if (this.companyChoosableValue) {
      if (obj.company) {
        document.getElementById(`invoice_${type}_company_true`).click()
      } else {
        document.getElementById(`invoice_${type}_company_false`).click()
      }
    }
  }

  toggle() {
    this.toggleTransition()
    if (this.eventCreated === false) {
      this.inputTarget.value = ""
      this.inputTarget.focus()

      if (this.hasOutsideCloseTarget) {
        const fn = this.clickOutside.bind(this)
        this.eventCreated = fn
        document.addEventListener("click", fn)
      }
    } else {
      this.removeEvent()
    }
  }

  hide() {
    this.leave()
    this.removeEvent()
    this.menuTarget.classList.add("hidden")
    this.listTarget.innerHTML = ""
  }

  clickOutside() {
    if (!this.hasMenuTarget) return

    if (!this.menuTarget.classList.contains("hidden")) {
      this.hide()
    }
  }

  removeEvent() {
    if (this.eventCreated !== false) {
      document.removeEventListener("click", this.eventCreated)
      this.eventCreated = false
    }
  }

  disconnect() {
    this.removeEvent()
  }

  add() {
    this.update()
    this.clearForm()

    if (this.companyValue) {
      trySetValue(`#${this.prefix}_name`, this.inputValue)
    } else {
      trySetValue(`#${this.prefix}_first_name`, this.inputValue)
    }

    this.hide()
  }

  clearForm() {
    document.querySelectorAll(`input[type="text"][id^=${this.prefix}_`).forEach((elem) => { elem.value = null })
  }

  isValidNip() {
    const nip = this.inputValue.replace(/[\s-]/g, "")

    if (!/^\d{10}$/.test(nip)) {
      return false
    }

    const weights = [6, 5, 7, 2, 3, 4, 5, 6, 7]
    const sum = weights
      .map((weight, index) => weight * parseInt(nip[index]))
      .reduce((a, b) => a + b, 0)

    const control = sum % 11
    return control === parseInt(nip[9]) && this.companyValue
  }

  companySetName() {
    document.getElementById(`${this.prefix}_name`).value = document.getElementById(`${this.prefix}_first_name`).value +
      " " + document.getElementById(`${this.prefix}_last_name`).value
  }

  update() {
    switch (this.typeValue) {
      case "buyer":
        trySetValue(`#invoice_client_id`, null)
        if (this.hasInvoiceOutlet && this.invoiceOutlet.accountUseMassPaymentValue) {
          trySetValue(`#${this.prefix}_mass_payment_code`, null)
          document.getElementById(`${this.prefix}_mass_payment_code`).dispatchEvent(new InputEvent("change"))
        }
        break
      case "recipient":
        trySetValue(`#${this.prefix}_client_id`, null)
        break
      case "issuer":
        trySetValue(`#${this.prefix}_contractor_id`, null)
        break
      default:
        throw new Error(`unknown type: ${this.typeValue}`)
    }
  }
}
